import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Rss } from 'lucide-react';
import { LAUNCHES, markLatestSeen } from '@/data/featureLaunches';

const PAGE_URL = 'https://docsite.care/whats-new';
const PAGE_TITLE = "What's New on DocSite — Feature Launch Timeline for Doctors";
const PAGE_DESC = 'Every product launch on DocSite — the SaaS platform for individual doctors. Verified patient inquiries, organic-SEO publishing, frictionless booking, and more — shipped on a transparent timeline.';

/**
 * Public timeline of all DocSite feature launches. Soft-404-safe (content
 * bundled in JS, no API roundtrip required for first paint). JSON-LD
 * `ItemList` schema + Article entries help Google interpret the timeline
 * as a structured changelog and surface fresh entries faster.
 */
export default function WhatsNewIndexPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    markLatestSeen();
  }, []);

  // Schema.org ItemList — every launched feature as a listed Article. Helps
  // Google crawl the timeline as a structured changelog and bubble fresh
  // entries into Discover / News for high-intent queries.
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DocSite Feature Launches',
    description: 'Every product launch on DocSite, in reverse-chronological order.',
    url: PAGE_URL,
    itemListElement: LAUNCHES.map((l, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://docsite.care${l.href}`,
      name: l.title,
    })),
  };

  // Schema.org BreadcrumbList — home → What's New
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'DocSite', item: 'https://docsite.care' },
      { '@type': 'ListItem', position: 2, name: "What's New", item: PAGE_URL },
    ],
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={PAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        {/* RSS auto-discovery — feed-readers + Google News pick this up */}
        <link rel="alternate" type="application/rss+xml" title="DocSite Feature Launches" href="https://docsite.care/whats-new/feed.xml" />
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 md:py-14">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 mb-4" aria-label="Breadcrumb" data-testid="whatsnew-breadcrumb">
          <Link to="/" className="hover:text-slate-700">Home</Link> <span className="mx-1.5">›</span> <span className="text-slate-700 font-medium">What's New</span>
        </nav>

        {/* Hero */}
        <header className="max-w-4xl mb-10">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-sky-600 flex items-center gap-1.5">
            <Sparkles size={12} /> What's New on DocSite
          </p>
          <h1 className="font-medium text-slate-900 mt-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.25rem, 4vw, 4.5rem)', lineHeight: 1.05 }}>
            Every feature, every launch — in the open.
          </h1>
          <p className="text-base text-slate-600 mt-4 max-w-2xl leading-relaxed">
            DocSite ships small, frequent improvements that compound into real outcomes for individual
            doctors — verified patient inquiries, organic-search visibility, frictionless booking. Below is
            the complete launch timeline. Newest first.
          </p>
          <a href="/whats-new/feed.xml" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 mt-3" data-testid="whatsnew-rss">
            <Rss size={11} /> Subscribe via RSS
          </a>
        </header>

        {/* Timeline */}
        <ol className="max-w-4xl space-y-6" data-testid="whatsnew-timeline">
          {LAUNCHES.map((l) => (
            <li key={l.slug} className="relative pl-8 md:pl-12 print-row" data-testid={`whatsnew-entry-${l.slug}`}>
              <span className="absolute left-0 top-2 w-3 h-3 rounded-full bg-sky-500 ring-4 ring-sky-100" aria-hidden="true" />
              <span className="absolute left-[5px] top-7 bottom-[-1.5rem] w-0.5 bg-slate-200" aria-hidden="true" />

              <article className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6 hover:border-sky-300 hover:shadow-sm transition">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <time dateTime={l.published_at} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    {new Date(l.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </time>
                  {l.isNew && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" aria-label="New launch">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      </span>
                      New
                    </span>
                  )}
                  {l.badge && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold uppercase tracking-wider">{l.badge}</span>
                  )}
                </div>

                <h2 className="text-xl md:text-2xl font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Link to={l.href} className="hover:text-sky-700 transition-colors" data-testid={`whatsnew-link-${l.slug}`}>{l.title}</Link>
                </h2>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-2xl">{l.summary}</p>
                <div className="mt-3">
                  <Link to={l.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900" data-testid={`whatsnew-cta-${l.slug}`}>
                    Read the 1-pager <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            </li>
          ))}
        </ol>

        {/* Footer CTA */}
        <div className="max-w-4xl mt-12 rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-3" data-testid="whatsnew-footer-cta">
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Get the next launch in your inbox.</p>
            <p className="text-xs text-slate-500 mt-0.5">Or just create your DocSite practice — every new feature ships free of cost on your existing plan.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/register?as=doctor&from=whats_new_index" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800" data-testid="whatsnew-footer-register">
              Start free in 3 clicks <ArrowRight size={14} />
            </Link>
            <Link to="/brochure" className="inline-flex items-center px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300" data-testid="whatsnew-footer-brochure">
              See everything DocSite includes
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
